import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  useTenant,
  useTenantModules,
  useSetTenantModule,
  useTenantSubscription,
  useSetSubscriptionStatus,
  useTenantCheckout,
  usePlans,
  useSetTenantPlan,
  type CompanyModule,
} from './tenants.api';

const money = (n: number, currency: string) =>
  new Intl.NumberFormat('es-PE', { style: 'currency', currency }).format(n);
const SUB_STATUSES = ['active', 'suspended', 'cancelled', 'trial'] as const;

function Toggle({
  enabled,
  disabled,
  onChange,
}: {
  enabled: boolean;
  disabled?: boolean;
  onChange: (n: boolean) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
        enabled ? 'bg-primary' : 'bg-muted',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      <span
        className={cn(
          'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
          enabled ? 'translate-x-5' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

export function TenantDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<'modules' | 'subscription'>('modules');
  const { data: tenant } = useTenant(id);
  const { data: modules } = useTenantModules(id);
  const setModule = useSetTenantModule(id);
  const { data: sub } = useTenantSubscription(id);
  const setStatus = useSetSubscriptionStatus(id);
  const checkout = useTenantCheckout(id);
  const { data: plans } = usePlans();
  const setPlan = useSetTenantPlan(id);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Link to="/empresas" className="h-9 w-9 grid place-items-center rounded-full hover:bg-accent">
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-xl font-bold tracking-tight">{tenant?.businessName ?? '…'}</h1>
          {tenant && <p className="text-xs text-muted-foreground">RUC {tenant.ruc}</p>}
        </div>
      </div>

      <div className="flex gap-1 border-b">
        {(['modules', 'subscription'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === t ? 'border-primary text-primary' : 'border-transparent text-muted-foreground',
            )}
          >
            {t === 'modules' ? 'Modulos' : 'Suscripcion y pagos'}
          </button>
        ))}
      </div>

      {tab === 'modules' && (
        <ul className="rounded-2xl border border-border bg-card divide-y shadow-sm">
          {(modules ?? []).map((m: CompanyModule) => (
            <li key={m.key} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <p className="font-medium">{m.name}</p>
                <p className="text-xs text-muted-foreground">
                  {m.category}
                  {m.dependsOn.length > 0 && ` · requiere: ${m.dependsOn.join(', ')}`}
                </p>
              </div>
              {m.isCore ? (
                <span className="text-xs font-medium text-muted-foreground">Siempre activo</span>
              ) : (
                <Toggle
                  enabled={m.enabled}
                  disabled={setModule.isPending}
                  onChange={(n) => setModule.mutate({ key: m.key, enabled: n })}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {tab === 'subscription' && sub && (
        <div className="rounded-2xl border border-border bg-card p-5 shadow-sm space-y-5 max-w-lg">
          {/* Selector de plan */}
          <div>
            <p className="text-sm font-medium mb-2">Plan</p>
            <div className="grid grid-cols-3 gap-2">
              {(plans ?? []).map((p) => (
                <button
                  key={p.key}
                  type="button"
                  disabled={setPlan.isPending}
                  onClick={() => setPlan.mutate(p.key)}
                  className={cn(
                    'rounded-xl border p-3 text-left transition-colors',
                    sub.planKey === p.key
                      ? 'border-primary ring-1 ring-primary bg-accent'
                      : 'border-border hover:bg-accent',
                  )}
                >
                  <p className="text-sm font-semibold">{p.name}</p>
                  <p className="text-xs text-muted-foreground tabular-nums">
                    {money(p.priceMonthly, sub.currency)}/mes
                  </p>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between border-t pt-4">
            <div>
              <p className="text-sm text-muted-foreground">Cobro mensual</p>
              <p className="text-2xl font-bold tabular-nums">{money(sub.total, sub.currency)}</p>
              <p className="text-xs text-muted-foreground">
                Plan {sub.planName}
                {sub.extras.length > 0 && ` + ${sub.extras.length} extra(s)`}
                {sub.includedModules.length > 0 && ` · incluye: ${sub.includedModules.join(', ')}`}
              </p>
            </div>
            <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium">{sub.status}</span>
          </div>

          <div className="flex flex-wrap gap-2">
            {SUB_STATUSES.map((s) => (
              <button
                key={s}
                type="button"
                disabled={setStatus.isPending || sub.status === s}
                onClick={() => setStatus.mutate(s)}
                className={cn(
                  'rounded-md border px-2.5 py-1 text-xs font-medium',
                  sub.status === s ? 'border-primary text-primary' : 'text-muted-foreground hover:bg-accent',
                )}
              >
                {s}
              </button>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            disabled={checkout.isPending}
            onClick={() =>
              checkout.mutate(undefined, {
                onSuccess: (d) => {
                  if (d.initPoint) {
                    void navigator.clipboard?.writeText(d.initPoint);
                    toast.success('Link de pago generado y copiado');
                    window.open(d.initPoint, '_blank');
                  }
                },
              })
            }
          >
            {checkout.isPending ? 'Generando…' : 'Generar link de pago (Mercado Pago)'}
          </Button>
        </div>
      )}
    </div>
  );
}
